<template>
  <div>
    <vue-data-table
      :headers="headers"
      :data="consignments"
      :items-per-page="10"
      :buttons="buttons"
      :summable-columns="summableColumns"
      :filter-data="filter"
      :filter-fields="consignmentFilterFields"
      :table-actions="consignmentTableActions"
      @filter="onConsignmentTableFilter"
      @reset="onConsignmentTableReset"
    >
      <template #cell-remark="{ row }">
        <span
          v-show="row.remark"
          class="px-2 py-1 rounded"
          :class="row.remark ? 'bg-light-info text-info' : ''"
        >
          <i class="bi bi-exclamation-circle-fill"></i>
        </span>
      </template>

      <template #cell-items="{ row }">
        <div v-if="row.items && row.items.length > 0">
          <div v-for="(item, index) in row.items" :key="index" class="mb-1">
            <span class="badge bg-primary">{{
              item.itemFullName || item.item_reference
            }}</span>
          </div>
        </div>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-itemExchangeRates="{ row }">
        <div v-if="row.itemExchangeRates && row.itemExchangeRates.length > 0">
          <div
            v-for="(rate, index) in row.itemExchangeRates"
            :key="index"
            class="mb-1"
          >
            <span class="badge bg-info">{{ parseFloat(rate).toFixed(4) }}</span>
          </div>
        </div>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-profitMargin="{ row }">
        <div class="d-flex align-items-center gap-2">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            v-model.number="row.profit_margin"
            @change="updateProfitMargin(row)"
            class="form-control form-control-sm"
            style="width: 80px"
            placeholder="7.00"
          />
          <span class="text-muted">%</span>
        </div>
      </template>

      <template #cell-sellingPrice="{ row }">
        <span class="px-2 py-1 rounded bg-light-success text-success fw-bold">
          {{ getSellingPrice(row).toFixed(2) }} MAD
        </span>
      </template>
    </vue-data-table>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
export default {
  data() {
    return {
      filter: {
        society_reference: null,
        startDate: null,
        endDate: null,
        item_reference: null,
        user_reference: null,
        cost_reference: null,
        invoice_number: "",
        supplier_reference: null,
      },
      search_value: "",
      addedToday: false,
      sortValue: "",
      selections: [],
      headers: [
        { field: "reference", label: "Réf.", class: "" },
        { field: "number", label: "N°", class: "" },
        { field: "date", label: "Date", class: "" },
        { field: "societyFullName", label: "Société", class: "" },
        { field: "itemExchangeRates", label: "Taux", class: "" },
        { field: "items", label: "Articles", class: "" },
        {
          field: "totalCostsHT",
          label: "Total HT",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "totalItemsQuantity",
          label: "Qty",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "costPerItem",
          label: "Prix/Art",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "profitMargin",
          label: "Marge %",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "sellingPrice",
          label: "Prix vente",
          class: "fw-bold",
          isAmount: true,
        },
        { field: "remark", label: "NB", class: "" },
      ],
      // Button actions
      buttons: [
        {
          label: "Détails",
          icon: "bi bi-info-square",
          class: "btn btn-primary",
          action: (consignment) => {
            this.$router.push({
              name: "consignments-details",
              params: { reference: consignment.reference },
            });
          },
        },
      ],
      // Summable columns
      summableColumns: [],
    };
  },
  computed: {
    ...mapGetters("consignment", {
      allConsignments: "getAll",
    }),
    ...mapGetters("society", {
      societies: "getAll",
    }),
    ...mapGetters("item", {
      items: "getAll",
    }),
    ...mapGetters("user", {
      users: "getAll",
    }),
    ...mapGetters("cost", {
      costs: "getAll",
    }),
    ...mapGetters("supplier", {
      suppliers: "getAll",
    }),
    consignments() {
      if (this.$userRole === "Magasinier") {
        return this.allConsignments;
      }
      return this.allConsignments;
    },
    consignmentFilterFields() {
      return [
        {
          type: "select",
          label: `${this.$t("sale7")} *`,
          model: "society_reference",
          placeholder: "Société",
          options: this.societies || [],
          labelColumn: "fullName",
          valueColumn: "reference",
        },
        {
          type: "select",
          label: "Article",
          model: "item_reference",
          placeholder: "Tous les articles",
          options: this.items || [],
          labelColumn: "fullName",
          valueColumn: "reference",
        },
        {
          type: "select",
          label: "Créé par",
          model: "user_reference",
          placeholder: "Tous les utilisateurs",
          options: this.users || [],
          labelColumn: "name",
          valueColumn: "reference",
        },
        {
          type: "select",
          label: "Facture (type)",
          model: "cost_reference",
          placeholder: "Tous les types",
          options: this.costs || [],
          labelColumn: "fullName",
          valueColumn: "reference",
        },
        {
          type: "text",
          label: "N° facture",
          model: "invoice_number",
          placeholder: "Recherche…",
        },
        {
          type: "select",
          label: "Fournisseur",
          model: "supplier_reference",
          placeholder: "Tous les fournisseurs",
          options: this.suppliers || [],
          labelColumn: "fullName",
          valueColumn: "reference",
        },
      ];
    },
    consignmentTableActions() {
      const actions = [
        {
          title: "Actualiser",
          icon: "bi bi-arrow-clockwise",
          class: "btn btn-secondary",
          action: () => this.refresh(),
        },
      ];
      if (this.$userRole !== "Magasinier") {
        actions.push({
          title: "Créer un dossier d'importation",
          icon: "bi bi-plus-square",
          class: "btn btn-primary",
          action: () => this.$router.push({ name: "consignments-create" }),
        });
      }
      return actions;
    },
  },

  created() {
    this.$store.dispatch("society/getAll");
    this.$store.dispatch("item/getAll");
    this.$store.dispatch("user/getAll");
    this.$store.dispatch("cost/getAll");
    this.$store.dispatch("supplier/getAll");
  },

  mounted() {
    const q = this.$route.query;
    const hasQuery =
      q.society_reference ||
      q.startDate ||
      q.endDate ||
      q.item_reference ||
      q.user_reference ||
      q.cost_reference ||
      (q.invoice_number && String(q.invoice_number).trim()) ||
      q.supplier_reference;

    if (hasQuery) {
      this.filter = {
        society_reference: q.society_reference || null,
        startDate: q.startDate || null,
        endDate: q.endDate || null,
        item_reference: q.item_reference || null,
        user_reference: q.user_reference || null,
        cost_reference: q.cost_reference || null,
        invoice_number: q.invoice_number ? String(q.invoice_number).trim() : "",
        supplier_reference: q.supplier_reference || null,
      };

      this.toFilter(this.filter);
    } else {
      this.$store.dispatch("consignment/getAll");
    }
  },
  methods: {
    async onConsignmentTableFilter(payload) {
      this.filter = { ...this.filter, ...payload };
      await this.toFilter(this.filter);
    },
    async onConsignmentTableReset() {
      this.filter = {
        society_reference: null,
        startDate: null,
        endDate: null,
        item_reference: null,
        user_reference: null,
        cost_reference: null,
        invoice_number: "",
        supplier_reference: null,
      };
      await this.$router.push({ name: "consignments-index", query: {} });
      await this.$store.dispatch("consignment/getAll");
    },
    buildQueryFromFilter(data) {
      const q = {};
      const keys = [
        "society_reference",
        "startDate",
        "endDate",
        "item_reference",
        "user_reference",
        "cost_reference",
        "supplier_reference",
      ];
      keys.forEach((k) => {
        const v = data[k];
        if (v !== null && v !== undefined && v !== "") {
          q[k] = v;
        }
      });
      if (data.invoice_number && String(data.invoice_number).trim()) {
        q.invoice_number = String(data.invoice_number).trim();
      }
      return q;
    },

    hasActiveFilters() {
      const f = this.filter;
      return (
        !!(
          f.society_reference ||
          f.startDate ||
          f.endDate ||
          f.item_reference ||
          f.user_reference ||
          f.cost_reference ||
          f.supplier_reference
        ) || !!(f.invoice_number && String(f.invoice_number).trim())
      );
    },

    async refresh() {
      if (this.hasActiveFilters()) {
        await this.$store.dispatch("consignment/filter", this.filter);
      } else {
        await this.$store.dispatch("consignment/getAll");
      }
    },

    async toFilter(data) {
      await this.$store.dispatch("consignment/filter", data);
      await this.$router.push({
        name: "consignments-index",
        query: this.buildQueryFromFilter(data),
      });
    },

    formatDate(value) {
      if (!value) return null;
      return moment(value).format("DD-MM-YYYY");
    },
    formatTime(value) {
      if (!value) return null;
      return moment(value).format("HH:mm");
    },
    // Get profit margin percentage (default 7% if not set)
    getProfitMargin(consignment) {
      return parseFloat(consignment.profit_margin || 7.0);
    },
    // Update profit margin for a consignment
    async updateProfitMargin(consignment) {
      try {
        const profitMargin = parseFloat(consignment.profit_margin || 7.0);
        if (isNaN(profitMargin) || profitMargin < 0 || profitMargin > 100) {
          // Reset to default if invalid
          consignment.profit_margin = 7.0;
          return;
        }
        await this.$api.put(`/consignments/${consignment.reference}`, {
          profit_margin: profitMargin,
        });
        // Update the consignment in the store
        await this.refresh();
      } catch (error) {
        console.error("Error updating profit margin:", error);
        await this.refresh();
      }
    },
    // Calculate selling price = costPerItem * (1 + profitMargin/100)
    getSellingPrice(consignment) {
      const costPerItem = parseFloat(consignment.costPerItem || 0);
      const profitMargin = this.getProfitMargin(consignment);
      if (costPerItem === 0) return 0;
      return costPerItem * (1 + profitMargin / 100);
    },
  },
};
</script>
